/*#
# The verbs (agent API)

Agents talk to virta through a small set of **verbs**, each declared once with
a tosijs-schema input schema. That one table drives validation at the write
boundary, the MCP tool catalogue, and this page. Adding a verb means adding a
row; there is no second list to keep in sync.

```js
import { MemoryLog, VirtaStore, callVerb, verbCatalog } from 'tosijs-virta'

const store = new VirtaStore({ log: new MemoryLog(), identity: 'agent:demo' })
await store.load()

const created = await callVerb(store, 'create', { title: 'from an agent', tags: ['source:demo'] })
const rejected = await callVerb(store, 'create', { title: 42 })
const listed = await callVerb(store, 'query', { filter: 'source:demo' })

preview.textContent = [
  `verbs: ${verbCatalog().map((v) => v.name).join(' ')}`,
  `created: ${created.ok ? '#' + created.result.num : created.error}`,
  `rejected: ${rejected.ok ? 'no' : rejected.error}`,
  `listed: ${listed.ok ? listed.result.length : listed.error}`,
].join('\n')
```
```test
test('valid input runs, invalid input is refused with a reason, query sees the write', () => {
  const lines = preview.textContent.split('\n')
  expect(lines[0]).toContain('create')
  expect(lines[1]).toBe('created: #1')
  expect(lines[2]).toMatch(/^rejected: /)
  expect(lines[2]).not.toBe('rejected: no')
  expect(lines[3]).toBe('listed: 1')
})
```
*/

/*{ "parent": "Reference", "order": 70 }*/

import { s, validate, type Infer } from 'tosijs-schema'
import type {
  AttachedFile,
  Attachment,
  ProjectProfile,
  ProjectSettings,
  Task,
  VirtaEvent,
} from './model'
import type { VirtaStore } from './store'
import { mergeEntries, splitEntry, supersedeEntry } from './practices'
import { planDispatch, recordPoke, type DispatchPlan } from './dispatch'
import { guide, type Guide } from './guidance'
import { similarTasks } from './similar'
import {
  pinSkill,
  publishSkill,
  readSkill,
  runSkill,
  SKILL_TYPE,
  skillSummaries,
  unpinSkill,
  type Published,
  type SkillRead,
  type SkillSummary,
} from './skills'
import {
  adoptNotice,
  declineNotice,
  noticeSummaries,
  type NoticeSummary,
} from './notices'
import {
  exportPractices,
  restorePractices,
  type PracticesExport,
  type RestorePlan,
} from './export'
import {
  ENDING_FOR,
  MOVE_RELATIONS,
  SORT_LABELS,
  type MoveRelation,
  type SortKey,
  joinedTags,
  swallowedFlag,
} from './tags'
import {
  importProject,
  pushToGitHub,
  type ImportResult,
  type PushResult,
} from './github'
import { importNotes, type NotesImportResult } from './notes'
import {
  bodyVersions,
  describeEvent,
  type BodyVersion,
  type TranscriptEntry,
} from './transcript'
import { COMMIT_LIMIT } from './remote'
import { tagValues, TYPE_KEY } from './tags'
import { brief, type Brief } from './brief'

const taskId = s.string.min(1)
const tags = s.array(s.string.min(1))

/** Task fields a tag key must not name, and the verb that changes each (board #3626). */
const FIELD_VERBS: Record<string, string> = {
  body: 'edit (virta edit <id> "<body>")',
  title: 'retitle (virta retitle <id> "<title>")',
}

/**
 * One row per verb. `input` is the wire contract; `run` is the whole
 * implementation. Descriptions are written for an agent choosing a tool.
 */
export const VERBS = {
  query: {
    description:
      'List tasks matching a tag filter (e.g. "project:tosijs-ui status:inbox is:any"). Empty filter = every open task. status:inbox matches tasks with no status tag.',
    input: s.object({
      filter: s.string.optional,
      sort: s.enum(Object.keys(SORT_LABELS) as SortKey[]).optional,
    }),
    run: (
      store: VirtaStore,
      i: { filter?: string; sort?: 'updated' | 'created' | 'num' | 'title' }
    ) => store.query(i.filter ?? '', i.sort ?? 'updated'),
  },
  get: {
    description: 'Fetch one task by id or by number (#38 → 38).',
    input: s.object({ id: s.string.optional, num: s.integer.optional }),
    run: (store: VirtaStore, i: { id?: string; num?: number }) => {
      const task =
        i.id !== undefined
          ? store.get(i.id)
          : i.num !== undefined
          ? store.byNum(i.num)
          : undefined
      if (!task) throw new Error(`no such task`)
      return task
    },
  },
  versions: {
    description:
      "Every version of a task's body, oldest first, each with a line diff from the one before (board #2473). An edit made from an older version — someone else got there first — is listed too, marked concurrent: it's kept beside the body and flagged on the card until someone chooses or merges (an edit from the current body settles it).",
    input: s.object({ id: taskId }),
    run: (store: VirtaStore, i: { id: string }) =>
      bodyVersions(store.events(i.id), (e) => store.whoWrote(e)),
  },
  transcript: {
    description:
      'The full event stream for a task — every comment, tag change, edit — in order, attributed. Never folded for agents.',
    input: s.object({ id: taskId }),
    run: (store: VirtaStore, i: { id: string }) =>
      store.transcript(i.id, { fold: false }),
  },
  create: {
    description:
      'File a new task. Tags are "key" or "key:value". A task with no status: tag is in the inbox (the implicit status:inbox).',
    input: s.object({
      title: s.string.min(1),
      body: s.string.optional,
      tags: tags.optional,
    }),
    run: (
      store: VirtaStore,
      i: { title: string; body?: string; tags?: string[] }
    ) => store.create(i),
  },
  continue: {
    description:
      'Create a task that continues from another ("yes, and"): linked both ways with continues:#n / continued-by:#m, inheriting project, kind, area and priority — never status, size or working. Title defaults to the original\'s.',
    input: s.object({
      id: taskId,
      title: s.string.optional,
      body: s.string.optional,
      tags: tags.optional,
    }),
    run: (
      store: VirtaStore,
      i: { id: string; title?: string; body?: string; tags?: string[] }
    ) => store.continueFrom(i.id, i),
  },
  tag: {
    description: 'Add tags to a task.',
    input: s.object({ id: taskId, tags: tags.min(1) }),
    run: (store: VirtaStore, i: { id: string; tags: string[] }) =>
      store.tag(i.id, ...i.tags),
  },
  untag: {
    description: 'Remove tags from a task.',
    input: s.object({ id: taskId, tags: tags.min(1) }),
    run: (store: VirtaStore, i: { id: string; tags: string[] }) =>
      store.untag(i.id, ...i.tags),
  },
  setTag: {
    description:
      'Replace every "key:*" tag with "key:value", or several keys in one commit (status=ready priority=high). No value clears the key. Moves a task between lanes. Over the API, pass `pairs` as "key=value" strings (["status=ready", "priority=high"]; "key=" clears it).',
    input: s.object({
      id: taskId,
      key: s.string.optional,
      value: s.string.optional,
      pairs: s.array(s.string).optional,
    }),
    run: (
      store: VirtaStore,
      i: { id: string; key?: string; value?: string; pairs?: string[] }
    ) => {
      const values: Record<string, string | null> = {}
      if (i.key) values[i.key] = i.value ?? null
      // "status=ready priority=high" arriving as ONE string is two pairs
      // that a shell didn't split (board #2426): split before each " key=".
      // A value with plain spaces ("title=two words") stays whole
      const pairs = (i.pairs ?? []).flatMap((p) =>
        p.split(/\s+(?=[\w.-]+=)/).filter(Boolean)
      )
      for (const pair of pairs) {
        const at = pair.indexOf('=')
        if (at < 1) throw new Error(`"${pair}" is not key=value`)
        const value = pair.slice(at + 1)
        values[pair.slice(0, at)] = value === '' ? null : value
      }
      if (!Object.keys(values).length)
        throw new Error('setTag needs a key, or pairs as key=value')
      return store.setTags(i.id, values)
    },
  },
  comment: {
    description: 'Add a comment to a task.',
    input: s.object({ id: taskId, text: s.string.min(1) }),
    run: (store: VirtaStore, i: { id: string; text: string }) =>
      store.comment(i.id, i.text),
  },
  retitle: {
    description: 'Change a task title.',
    input: s.object({ id: taskId, title: s.string.min(1) }),
    run: (store: VirtaStore, i: { id: string; title: string }) =>
      store.retitle(i.id, i.title),
  },
  edit: {
    description:
      'Replace a task body (markdown). The old body stays in the transcript.',
    input: s.object({ id: taskId, body: s.string }),
    run: (store: VirtaStore, i: { id: string; body: string }) => {
      // a skill's text is instructions every agent follows (board #2477):
      // it changes through the one door that checks who may
      const task = store.get(i.id)
      if (task && tagValues(task.tags, TYPE_KEY)[0] === SKILL_TYPE)
        throw new Error(
          `#${task.num} is a skill: change it with skill publish (virta skill publish <name> <SKILL.md> [files…]), which checks who may and keeps its files with its text`
        )
      return store.edit(i.id, i.body)
    },
  },
  close: {
    description:
      'Close a task, optionally with a reason. `as` is how it ended: done (the default); obsolete, or the kinds of obsolete — superseded, duplicate, absorbed; rejected, or not-a-task. Obsolete and rejected tasks are archived; a kind is kept as a closed:<as> tag. A practice, policy or guidance is never done: close it as retired with the reason (or split, supersede or merge it).',
    input: s.object({
      id: taskId,
      reason: s.string.optional,
      as: s.enum(Object.keys(ENDING_FOR)).optional,
    }),
    run: (store: VirtaStore, i: { id: string; reason?: string; as?: string }) =>
      store.close(i.id, i.reason, i.as),
  },
  retract: {
    description:
      "Take back one of your own comments: it stays in the transcript, marked retracted (its text still readable, for any reply that depends on it). With no comment id, your latest comment on the task. undo: true restores a retracted one. Only the comment's author can retract it.",
    input: s.object({
      id: taskId,
      comment: s.string.optional,
      reason: s.string.optional,
      undo: s.boolean.optional,
    }),
    run: (
      store: VirtaStore,
      i: { id: string; comment?: string; reason?: string; undo?: boolean }
    ) =>
      store.retract(i.id, {
        comment: i.comment,
        reason: i.reason,
        undo: i.undo,
      }),
  },
  review: {
    description:
      'Hand finished work to a person to look at: note says what changed and what to check. One commit: the note as a comment, status:review, and nobody working on it. Use it when the work needs a look before it counts (anything user-facing, a design change, anything you are unsure of); close it instead when there is nothing to check.',
    input: s.object({ id: taskId, note: s.string.min(1) }),
    run: (store: VirtaStore, i: { id: string; note: string }) =>
      store.review(i.id, i.note),
  },
  reopen: {
    description: 'Reopen a closed task.',
    input: s.object({ id: taskId }),
    run: (store: VirtaStore, i: { id: string }) => store.reopen(i.id),
  },
  subscribe: {
    description: 'Subscribe the calling identity (or a named one) to a task.',
    input: s.object({ id: taskId, subscriber: s.string.optional }),
    run: (store: VirtaStore, i: { id: string; subscriber?: string }) =>
      store.subscribe(i.id, i.subscriber),
  },
  unsubscribe: {
    description:
      'Unsubscribe the calling identity (or a named one) from a task.',
    input: s.object({ id: taskId, subscriber: s.string.optional }),
    run: (store: VirtaStore, i: { id: string; subscriber?: string }) =>
      store.unsubscribe(i.id, i.subscriber),
  },
  attach: {
    description:
      'Attach a file or a link to a task: `virta attach #n ./shot.png`. A file (images, video, audio, PDF, markdown, text; up to 25 MB) is stored privately on the host: from a shell, `virta attach #n ./shot.png`; over MCP, content (base64) and name. A link (url, http(s)) is kept as a link. Or name a file already stored (file: { area, path, type, bytes, sha256 }, as PUT /blob answered) (board #2436).',
    input: s.object({
      id: taskId,
      name: s.string.min(1),
      url: s.string.pattern(/^https?:\/\//).optional,
      file: s.object({
        area: s.string.pattern(/^virta:files$/),
        path: s.string.pattern(
          /^[A-Za-z0-9][A-Za-z0-9._-]*(\/[A-Za-z0-9][A-Za-z0-9._-]*)*$/
        ),
        type: s.string.pattern(/^[\w.+-]+\/[\w.+-]+$/),
        bytes: s.integer,
        sha256: s.string.pattern(/^[0-9a-f]{64}$/),
      }).optional,
    }),
    run: (
      store: VirtaStore,
      i: { id: string; name: string; url?: string; file?: AttachedFile }
    ) => {
      if (!i.url === !i.file)
        throw new Error(
          'attach takes a url (a link) or a file (a stored file), one of them'
        )
      return store.attach(i.id, i.name, i.url ?? '', i.file)
    },
  },
  attachments: {
    description:
      'What a task carries (board #3560): each attachment, oldest first, as { name, file: { area, path, type, bytes, sha256 } } for a stored file or { name, url } for a link, with when and who. From a shell, `virta files #n --out ./dir` downloads the files and checks each against its sha256; over MCP, pass content: true for small files inline.',
    input: s.object({ id: taskId, name: s.string.optional }),
    run: (store: VirtaStore, i: { id: string; name?: string }) =>
      store.attachments(i.id, i.name),
  },
  procedure: {
    description:
      'What a practice does when it fires (board #2474): tasks matching `when` (a filter, e.g. "~crash -kind") get the tags in `set` (e.g. ["kind:bug"]). In suggest mode it proposes them; in automatic mode it sets them, undoable in one step. It fires on its trigger: tags (trigger:enters-inbox, trigger:status:ready, trigger:tagged:kind, …), only in projects that opted in (project set <name> --practices "#n"). A procedure never ends, starts or archives a task.',
    input: s.object({
      id: taskId,
      when: s.string.optional,
      set: s.array(s.string.min(1)).min(1),
    }),
    run: (store: VirtaStore, i: { id: string; when?: string; set: string[] }) =>
      store.procedure(i.id, { when: i.when ?? '', set: i.set }),
  },
  resolve: {
    description:
      "Resolve a practice's work on a task (board #2474): accept its suggestion (the tags are set, as you), overrule it, or undo an automatic change (exactly what it did is reversed). `of` names the suggestion or change; left out, the one waiting on the task (the latest, for undo). Every outcome is counted on the practice.",
    input: s.object({
      id: taskId,
      outcome: s.enum(['accept', 'overrule', 'undo']),
      of: s.string.optional,
    }),
    run: (
      store: VirtaStore,
      i: { id: string; outcome: 'accept' | 'overrule' | 'undo'; of?: string }
    ) => {
      const task = store.get(i.id)
      if (!task) throw new Error('no such task')
      let of = i.of
      if (!of && i.outcome === 'undo') {
        const resolved = new Set(
          store
            .events(i.id)
            .filter((e) => e.kind === 'resolved')
            .map((e) => (e.payload as { of: string }).of)
        )
        of = store
          .events(i.id)
          .filter((e) => e.kind === 'practiced' && !resolved.has(e.id))
          .pop()?.id
        if (!of)
          throw new Error(
            `nothing a practice did on #${task.num} is left to undo`
          )
      } else if (!of) {
        if (task.pending.length !== 1)
          throw new Error(
            task.pending.length
              ? `${task.pending.length} suggestions wait on #${
                  task.num
                }: name one (of: ${task.pending.map((p) => p.id).join(', ')})`
              : `no suggestion waits on #${task.num}`
          )
        of = task.pending[0]!.id
      }
      const outcome =
        i.outcome === 'accept'
          ? 'accepted'
          : i.outcome === 'overrule'
          ? 'overruled'
          : 'undone'
      return store.resolve(i.id, of, outcome)
    },
  },
  runPractices: {
    description:
      'Run the practices (board #2474): for every project that opted in (or `project`), each practice in suggest or automatic mode whose trigger fired on a task since the project opted in proposes or applies its procedure. Idempotent: ids come from the practice and the event, so a second run writes nothing. `dryRun` reports without writing.',
    input: s.object({
      project: s.string.optional,
      dryRun: s.boolean.optional,
    }),
    run: (store: VirtaStore, i: { project?: string; dryRun?: boolean }) =>
      store.runPractices({ project: i.project, dry: i.dryRun }),
  },
  dispatch: {
    description:
      "Which agent sessions the dispatcher would poke now (board #161): for each project with dispatch on (a person switches it on in the board's project settings), whether a poke is due and why not when it isn't. Due means a person moved a task to ready since the last poke, the interval has passed and today's pokes are under the cap. `text` is the message to send. Reads only.",
    input: s.object({ project: s.string.optional }),
    run: (store: VirtaStore, i: { project?: string }) =>
      planDispatch(store, { project: i.project }),
  },
  poke: {
    description:
      "Record that the dispatcher poked a project's session (board #161), after sending it the plan's `text`: a comment on the task it named, so the board shows every poke. Refused when no poke is due.",
    input: s.object({ project: s.string.min(1) }),
    run: (store: VirtaStore, i: { project: string }) =>
      recordPoke(store, i.project),
  },
  similar: {
    description:
      'Is this already filed? (board #2732) The open tasks whose titles share words with `title`, most alike first: two shared words, or one that is rare among the open titles looked at. In `project` when given, else every open task. Reads only. Filing a task answers the same list with it, so this is for looking first; query with "~word" is the wider search (it reads bodies too).',
    input: s.object({
      title: s.string.min(1),
      project: s.string.optional,
      limit: s.integer.optional,
    }),
    run: (
      store: VirtaStore,
      i: { title: string; project?: string; limit?: number }
    ) => similarTasks(store.tasks, i),
  },
  guide: {
    description:
      'The way into the Library (board #2497). With no topic: the root entry (tagged index:root) and every topic with its index entry, so any topic is one hop away. With a topic: its index entry, then its entries for `project` in precedence order — project-scoped first, then topic, then global — with any default a narrower entry overrides (overrides:#n) marked as overridden. `text` is the same as markdown.',
    input: s.object({
      topic: s.string.optional,
      project: s.string.optional,
    }),
    run: (store: VirtaStore, i: { topic?: string; project?: string }) =>
      guide(store, i),
  },
  skills: {
    description:
      'Every skill the board serves (board #2477). Each with its name, entry number, newest served version, description, which projects pinned which version, which version each project ran last, and how many edits are ignored (written by a token that may not change it).',
    input: s.object({}),
    run: (store: VirtaStore) => skillSummaries(store),
  },
  skill: {
    description:
      "A skill as a project runs it (board #2477). Its text and files at the project's pinned version, or the newest; `version` names another. Says which version it is, whether it is pinned and how far behind, what changed since the project last ran it, and any edits that are ignored. In the text, {{skill-dir}} is where the caller keeps the files. Reads only: skill_run also records the run.",
    input: s.object({
      name: s.string.min(1),
      project: s.string.optional,
      version: s.number.optional,
    }),
    run: (
      store: VirtaStore,
      i: { name: string; project?: string; version?: number }
    ) => readSkill(store, i),
  },
  runSkill: {
    description:
      "Run a skill for a project and record the run (board #2477). What skill answers, and the board records the version the project ran (ran:<project>@<n> on the skill's entry, written only when it changes) — that record is how the next run says what changed. The installed stub runs this: virta skill run <name>.",
    input: s.object({ name: s.string.min(1), project: s.string.optional }),
    run: (store: VirtaStore, i: { name: string; project?: string }) =>
      runSkill(store, i),
  },
  publishSkill: {
    description:
      'Write a skill\'s text and files (board #2477). Its first version, or a new one every machine runs on its next run. `text` is the SKILL.md (front matter with name and description, then the instructions); `files` maps plain file names to their text (a workflow script); `note` is one line on what changed, shown to every project on its next run. Only the owner (signed in on the board) or an agent acting for a repo the owner named on the entry (the tag custodian:<repo>, added signed in) can change a skill: any other agent is refused with what to ask for, and a new skill it files is a draft nobody runs until then. The repo is the one you act as ("<machine> × <repo>"), so no token per repo is needed. Each version records the token that wrote it, and a version written with a different token than the earlier ones from that repo is flagged on every run. The stricter tag custodian:<token id> accepts one token only. The CLI reads the files: virta skill publish <name> <SKILL.md> [files…].',
    input: s.object({
      name: s.string.min(1),
      text: s.string.min(1),
      files: s.record(s.string).optional,
      note: s.string.optional,
      token: s.string.optional,
      project: s.string.optional,
    }),
    run: (
      store: VirtaStore,
      i: {
        name: string
        text: string
        files?: Record<string, string>
        note?: string
        token?: string
        project?: string
      }
    ) => publishSkill(store, i),
  },
  pinSkill: {
    description:
      'Pin a project to a version of a skill (board #2477). It runs that version — the newest, unless `version` names one — until unpinned, so a release in progress finishes on the version it started with. Each run says it is pinned and how far behind.',
    input: s.object({
      name: s.string.min(1),
      project: s.string.optional,
      version: s.number.optional,
    }),
    run: (
      store: VirtaStore,
      i: { name: string; project?: string; version?: number }
    ) => pinSkill(store, i),
  },
  unpinSkill: {
    description:
      "Remove a project's pin on a skill (board #2477). It runs the newest version again.",
    input: s.object({ name: s.string.min(1), project: s.string.optional }),
    run: (store: VirtaStore, i: { name: string; project?: string }) =>
      unpinSkill(store, i),
  },
  exportPractices: {
    description:
      'Export every practice, policy and guidance (board #2479), for a git repo to keep: `files` maps paths under registry/ to their contents — one <id>.md per entry (front matter: type, topics, mode, triggers, procedure, measure, evidence; then its text), README.md (the index), events.jsonl (every event, what restore replays) and manifest.json (numbers, project opt-ins). For a public repo: no host stamps, emails redacted. The CLI writes them: virta export <dir>.',
    input: s.object({}),
    run: (store: VirtaStore) => exportPractices(store),
  },
  restorePractices: {
    description:
      "Restore an export onto this host (board #2479): pass the export's events.jsonl and manifest.json as text. Writes only the events the host lacks (a second run writes nothing), renumbers tags that name an exported entry by number (chains-from:#12 → its number here), and restores the projects' opt-ins by id. `dryRun` reports without writing. The CLI reads them: virta restore <dir>.",
    input: s.object({
      events: s.string,
      manifest: s.string,
      dryRun: s.boolean.optional,
    }),
    run: (
      store: VirtaStore,
      i: { events: string; manifest: string; dryRun?: boolean }
    ) =>
      restorePractices(store, {
        events: i.events,
        manifest: i.manifest,
        dry: i.dryRun,
      }),
  },
  measure: {
    description:
      'Say how a practice (or policy) is judged (board #2472): measure — what success looks like, e.g. "share of triage suggestions accepted unchanged" — and death — the result that means retire or revise, e.g. "under 50% accepted after 40 applications". Both required. Replacing them is a new version; evidence stays tagged with the version it was collected under. Set type:practice on the issue first.',
    input: s.object({
      id: taskId,
      measure: s.string.min(1),
      death: s.string.min(1),
    }),
    run: (
      store: VirtaStore,
      i: { id: string; measure: string; death: string }
    ) => store.measure(i.id, i),
  },
  showcase: {
    description:
      'Give a closed task its showcase: a picture, a line and a link, shown in the board\'s carousel (`virta showcase #n "It works" --media ./shot.png`). Board #2439: a picture (required: from a shell `--media ./shot.png` uploads it; over MCP mediaContent (base64) and mediaName; or media, an http(s) URL of an image or video), text (required, at most 200 characters, **bold** the only markdown), and optionally title (defaults to the task\'s), alt (what the picture shows; defaults to "screen capture of <title>") and link (docs, demo, video). Showcasing again replaces it; withdraw takes it down. Works on any closed task, archived included, whenever the picture or the write-up exists.',
    input: s.object({
      id: taskId,
      text: s.string.optional,
      title: s.string.optional,
      media: s.string.optional,
      file: s.object({
        area: s.string.pattern(/^virta:files$/),
        path: s.string,
        type: s.string,
        bytes: s.integer,
        sha256: s.string,
      }).optional,
      alt: s.string.optional,
      link: s.string.optional,
      withdraw: s.boolean.optional,
    }),
    run: (
      store: VirtaStore,
      i: {
        id: string
        text?: string
        title?: string
        media?: string
        file?: AttachedFile
        alt?: string
        link?: string
        withdraw?: boolean
      }
    ) => store.showcase(i.id, { ...i, text: i.text ?? '' }),
  },
  archive: {
    description:
      "Archive tasks in one commit (hidden from the board unless a filter says archived): the board's 'archive all' on a lane. Unarchive with untag archived.",
    input: s.object({ ids: s.array(taskId).min(1) }),
    run: (store: VirtaStore, i: { ids: string[] }) => store.archive(i.ids),
  },
  notices: {
    description:
      "Ecosystem notices (board #2548): policies tagged targets:all or targets:<project> that every targeted project should adopt. With `project`: that project's notices, most important first, each owed, adopted or not-applicable. Without: every notice with which enrolled projects adopted it, declined it, or still owe it. The checks (check: tags) run in a repo, so only the CLI verifies them: `virta notices` there.",
    input: s.object({ project: s.string.optional }),
    run: (store: VirtaStore, i: { project?: string }) =>
      noticeSummaries(store, i.project),
  },
  adopt: {
    description:
      'Record that a project adopted a notice with no checks, on your word (board #2548). A notice with checks is adopted only by `virta project notices` run in the repo, which runs them and records what it saw (board #2839); this refuses one.',
    input: s.object({
      id: taskId,
      project: s.string.min(1),
    }),
    run: (store: VirtaStore, i: { id: string; project: string }) =>
      adoptNotice(store, i.id, i.project),
  },
  notApplicable: {
    description:
      'Declare a notice not applicable to a project, with the reason (board #2548): it stops being owed there, and the reason is a comment on the notice.',
    input: s.object({
      id: taskId,
      project: s.string.min(1),
      reason: s.string.min(1),
    }),
    run: (
      store: VirtaStore,
      i: { id: string; project: string; reason: string }
    ) => declineNotice(store, i.id, i.project, i.reason),
  },
  reattribute: {
    description:
      "Correct who a credential says it is (board #2752), by appending, never rewriting: the events stamped by `token` (through `through`, an ISO time; left out, its future writes too), or the listed `events`, show `label` in place of the token's own label (and in `by`'s machine part), and/or `agent` in place of `by`'s agent part — the repo after its ×, taken from the folder a command ran in (board #2830). It counts only when written with that same token, or by its principal signed in directly — the host's stamps prove both. The original stays visible: 'X (corrected from Y)'. The CLI fills in its own token: virta self relabel \"<label>\".",
    input: s.object({
      label: s.string.optional,
      agent: s.string.optional,
      token: s.string.optional,
      events: s.array(s.string.min(1)).optional,
      through: s.string.optional,
      reason: s.string.optional,
    }),
    run: (
      store: VirtaStore,
      i: {
        label?: string
        agent?: string
        token?: string
        events?: string[]
        through?: string
        reason?: string
      }
    ) => store.reattribute(i),
  },
  child: {
    description:
      "File a part of a parent task (board #755): a new task with the parent's project, kind, area and priority, tagged parent:#n. A parent is as complete as its least complete part, automatically: when a part moves, the parent's status follows in the same commit (lane order: inbox, freezer, backlog, ready, in-progress, review, done; obsolete and rejected parts don't count), it closes when every counting part is done, and its own status can't be set by hand. Make an existing task a part with setTag parent=#n; list parts with the filter parent:#n.",
    input: s.object({
      id: taskId,
      title: s.string.min(1),
      body: s.string.optional,
      tags: tags.optional,
    }),
    run: (
      store: VirtaStore,
      i: { id: string; title: string; body?: string; tags?: string[] }
    ) => store.child(i.id, { title: i.title, body: i.body, tags: i.tags }),
  },
  parent: {
    description:
      "File a parent for an existing task (board #755): a new task with that task's project, kind, area and priority, and the task made its part, in one commit — so the parent arrives at its part's status (a parent for an item already in progress is in progress; add more parts with setTag parent=#n and it takes the least advanced one's). See child for the rule.",
    input: s.object({
      id: taskId,
      title: s.string.min(1),
      body: s.string.optional,
      tags: tags.optional,
    }),
    run: (
      store: VirtaStore,
      i: { id: string; title: string; body?: string; tags?: string[] }
    ) => store.parentFor(i.id, { title: i.title, body: i.body, tags: i.tags }),
  },
  parts: {
    description:
      "Move a parent by moving its unfinished parts (board #755): every part of `id` that isn't done (down through nesting; obsolete and rejected ones aside) goes to `status` in one commit, and the parent follows them there. A parent never drags its parts on its own, and its status can't be set directly: this is how you put a whole piece of work in the freezer, back in the backlog, or close it (done, obsolete or rejected close the parts).",
    input: s.object({ id: taskId, status: s.string.min(1) }),
    run: (store: VirtaStore, i: { id: string; status: string }) =>
      store.moveParts(i.id, i.status),
  },
  split: {
    description:
      "Split a practice, policy or guidance into new ones (board #2496): each title becomes a successor with the same type, topics and project, tagged split-from:#A, and A is retired (closed as retired, saying what it split into). Successors don't inherit a mode, trigger or procedure: switch them on yourself. A's evidence stays on A; each successor shows it as earlier evidence and starts its own record.",
    input: s.object({ id: taskId, titles: s.array(s.string.min(1)).min(1) }),
    run: (store: VirtaStore, i: { id: string; titles: string[] }) =>
      splitEntry(store, i.id, i.titles),
  },
  supersede: {
    description:
      "Replace a practice, policy or guidance with another (board #2496): A (id) is tagged superseded-by:#B (by) and retired. B shows A's evidence as earlier evidence; nothing carries over that would make B act.",
    input: s.object({ id: taskId, by: s.string.min(1) }),
    run: (store: VirtaStore, i: { id: string; by: string }) =>
      supersedeEntry(store, i.id, i.by),
  },
  merge: {
    description:
      'Merge practices, policies or guidance into one (board #2496): each of ids is tagged merged-into:#C (into) and retired; C must be open, and shows their evidence as earlier evidence.',
    input: s.object({ ids: s.array(taskId).min(1), into: s.string.min(1) }),
    run: (store: VirtaStore, i: { ids: string[]; into: string }) =>
      mergeEntries(store, i.ids, i.into),
  },
  work: {
    description:
      'Say you are working on this task. You work on one thing at a time: this clears your "working:" tag from every other task unless also=true ("as well as what I was doing").',
    input: s.object({ id: taskId, also: s.boolean.optional }),
    run: (store: VirtaStore, i: { id: string; also?: boolean }) =>
      store.work(i.id, { also: i.also }),
  },
  stop: {
    description: 'Stop working on a task, or on everything if no id is given.',
    input: s.object({ id: taskId.optional }),
    run: (store: VirtaStore, i: { id?: string }) => store.stopWork(i.id),
  },
  move: {
    description:
      'Hand a task to another project: a task has one owner. relation says what the project it leaves keeps: "interested" (the default: it still sees the task on its board), "blocks" (it cannot proceed until this is done), "delays" (it can, more slowly), or "none". Adds from:<old> and <relation>:<old> tags and a comment.',
    input: s.object({
      id: taskId,
      project: s.string.min(1),
      relation: s.enum([...MOVE_RELATIONS]).optional,
    }),
    run: (
      store: VirtaStore,
      i: { id: string; project: string; relation?: MoveRelation }
    ) => store.move(i.id, i.project, i.relation),
  },
  brief: {
    description:
      "A session's opening digest for one project: tasks created or re-prioritised by others since `since` (the `cursor` a previous brief returned; 0 for a first look), open questions, what is in progress and by whom, and the top of ready by priority. `onBoard` is false unless the project enrolled itself (virta project onboard in its repo).",
    input: s.object({
      project: s.string.min(1),
      since: s.integer.optional,
      limit: s.integer.optional,
      token: s.string.optional,
    }),
    run: (
      store: VirtaStore,
      i: { project: string; since?: number; limit?: number; token?: string }
    ) => brief(store, i),
  },
  projects: {
    description:
      'Every project on the board with its settings (icon, color, links, version) and open/total task counts.',
    input: s.object({}),
    run: (store: VirtaStore) => store.projects,
  },
  project: {
    description:
      'Set project settings: icon (an emoji, an image URL, or an inline image such as data:image/svg+xml;base64,… — the CLI takes a file: --icon ./logo.svg), color (palette name or css color), background (css), description, site, package (npm), repo (owner/repo), version, practices (the practices it opts in to, e.g. "#12 #40"). Only the keys given change.',
    input: s.object({
      name: s.string.min(1),
      icon: s.string.optional,
      color: s.string.optional,
      background: s.string.optional,
      description: s.string.optional,
      site: s.string.optional,
      package: s.string.optional,
      repo: s.string.optional,
      version: s.string.optional,
      practices: s.string.optional,
    }),
    run: (
      store: VirtaStore,
      i: { name: string } & Partial<ProjectSettings>
    ) => {
      const { name, ...settings } = i
      return store.setProject(name, settings)
    },
  },
  import: {
    description:
      'Onboard a GitHub repo (owner/repo or URL): its description, homepage, package and version become project settings; its issues become tasks. Safe to repeat — only new issues are added.',
    input: s.object({
      repo: s.string.min(3),
      project: s.string.optional,
      token: s.string.optional,
    }),
    run: (
      store: VirtaStore,
      i: { repo: string; project?: string; token?: string }
    ) => importProject(store, i.repo, { project: i.project, token: i.token }),
  },
  githubPush: {
    description:
      "Carry imported tasks' outcomes back to GitHub: closed in virta closes the issue with the close reason as a comment (superseded / not-a-task / duplicate close it as not planned); reopened reopens it. Only GitHub-imported tasks; reconciles against the live issues, so repeating it changes nothing. Name the `project`, or pass all: true for every project on the board: it never guesses (board #3213). `repos` in the answer is the GitHub repos it compared against. `dryRun` reports what it would post. Needs a token.",
    input: s.object({
      project: s.string.optional,
      all: s.boolean.optional,
      token: s.string.optional,
      dryRun: s.boolean.optional,
    }),
    run: (
      store: VirtaStore,
      i: { project?: string; all?: boolean; token?: string; dryRun?: boolean }
    ) => pushToGitHub(store, i),
  },
  notes: {
    description:
      "Import a repo's TODO.md / UPSTREAM.md (any markdown list) as tasks: one per top-level item, done items closed, GitHub issue URLs as upstream:<repo>#n / github:n tags. Idempotent by item text. Pass the file's text; to carry the why, also its repo-relative path, repoUrl (the blob URL at the commit, ending in /: each body links its source line) and references (the review files it cites, path → text: a cited finding like M1 is quoted into the body).",
    input: s.object({
      project: s.string.min(1),
      text: s.string.min(1),
      file: s.string.optional,
      path: s.string.optional,
      repoUrl: s.string.optional,
      references: s.record(s.string).optional,
    }),
    run: (
      store: VirtaStore,
      i: {
        project: string
        text: string
        file?: string
        path?: string
        repoUrl?: string
        references?: Record<string, string>
      }
    ) => {
      const { text, ...options } = i
      return importNotes(store, text, options)
    },
  },
  apply: {
    description:
      'Many writes in one call (board #2408): `ops` is a list of { op, ...that verb\'s input }, e.g. { "op": "close", "id": "#12", "reason": "…", "as": "not-a-task" }, { "op": "set", "id": "#13", "status": "ready", "priority": "high" }, { "op": "comment", "id": "#14", "text": "…" }. An op is any write verb (or its short name: set, done); id may be #n or n. Each op sees the ones before it. All or nothing: if any op is refused, nothing is written and every refusal is listed. `dryRun` validates every op and reports what would change, writing nothing.',
    input: s.object({
      ops: s.array(s.record(s.any)).min(1),
      dryRun: s.boolean.optional,
    }),
    run: (
      store: VirtaStore,
      i: { ops: Record<string, unknown>[]; dryRun?: boolean }
    ): Promise<ApplyResult> => runApply(store, i),
  },
} as const

/**
 * What each verb is called (owner, 2026-09-28; board #2440): a task is the
 * implicit noun, so a task verb is a bare word (`virta close #42`); the
 * other noun comes first and modifies the verb (`virta project set x`).
 * One meaning per word, and no bare word that suggests something else
 * (`update` reinstalled the CLI; `log` read as a log file). A Record, so a
 * new verb can't ship unnamed. The CLI reads `noun` and `name`; MCP names a
 * task tool by its verb (unchanged) and a project tool `project_<name>`.
 */
export const VERB_NAMES: Readonly<
  Record<VerbName, { noun: 'task' | 'project' | 'skill'; name: string }>
> = {
  query: { noun: 'task', name: 'list' },
  get: { noun: 'task', name: 'show' },
  transcript: { noun: 'task', name: 'history' },
  versions: { noun: 'task', name: 'versions' },
  create: { noun: 'task', name: 'new' },
  continue: { noun: 'task', name: 'continue' },
  tag: { noun: 'task', name: 'tag' },
  untag: { noun: 'task', name: 'untag' },
  setTag: { noun: 'task', name: 'set' },
  comment: { noun: 'task', name: 'comment' },
  retitle: { noun: 'task', name: 'retitle' },
  edit: { noun: 'task', name: 'edit' },
  attachments: { noun: 'task', name: 'files' },
  close: { noun: 'task', name: 'close' },
  retract: { noun: 'task', name: 'retract' },
  review: { noun: 'task', name: 'review' },
  reopen: { noun: 'task', name: 'reopen' },
  subscribe: { noun: 'task', name: 'subscribe' },
  unsubscribe: { noun: 'task', name: 'unsubscribe' },
  attach: { noun: 'task', name: 'attach' },
  archive: { noun: 'task', name: 'archive' },
  child: { noun: 'task', name: 'child' },
  parent: { noun: 'task', name: 'parent' },
  parts: { noun: 'task', name: 'parts' },
  split: { noun: 'task', name: 'split' },
  reattribute: { noun: 'task', name: 'reattribute' },
  notices: { noun: 'project', name: 'notices' },
  adopt: { noun: 'project', name: 'adopt' },
  notApplicable: { noun: 'project', name: 'na' },
  supersede: { noun: 'task', name: 'supersede' },
  merge: { noun: 'task', name: 'merge' },
  showcase: { noun: 'task', name: 'showcase' },
  measure: { noun: 'task', name: 'measure' },
  procedure: { noun: 'task', name: 'procedure' },
  resolve: { noun: 'task', name: 'resolve' },
  runPractices: { noun: 'project', name: 'practices' },
  dispatch: { noun: 'project', name: 'dispatch' },
  poke: { noun: 'project', name: 'poke' },
  exportPractices: { noun: 'task', name: 'export' },
  guide: { noun: 'task', name: 'guide' },
  similar: { noun: 'task', name: 'similar' },
  skills: { noun: 'skill', name: 'list' },
  skill: { noun: 'skill', name: 'show' },
  runSkill: { noun: 'skill', name: 'run' },
  publishSkill: { noun: 'skill', name: 'publish' },
  pinSkill: { noun: 'skill', name: 'pin' },
  unpinSkill: { noun: 'skill', name: 'unpin' },
  restorePractices: { noun: 'task', name: 'restore' },
  work: { noun: 'task', name: 'work' },
  stop: { noun: 'task', name: 'unwork' },
  move: { noun: 'task', name: 'move' },
  apply: { noun: 'task', name: 'apply' },
  brief: { noun: 'project', name: 'brief' },
  projects: { noun: 'project', name: 'list' },
  project: { noun: 'project', name: 'set' },
  import: { noun: 'project', name: 'import' },
  notes: { noun: 'project', name: 'notes' },
  githubPush: { noun: 'project', name: 'push' },
}

/** A verb's MCP tool name: a task verb's own; a project verb's `project_<name>`. */
export const mcpToolName = (verb: VerbName): string =>
  VERB_NAMES[verb].noun === 'task'
    ? verb
    : `${VERB_NAMES[verb].noun}_${VERB_NAMES[verb].name}`

/** A tool name (current, or a verb's own name — the old one) → its verb, or undefined. */
export function verbForTool(tool: string): VerbName | undefined {
  if (Object.hasOwn(VERBS, tool)) return tool as VerbName
  return (Object.keys(VERBS) as VerbName[]).find((v) => mcpToolName(v) === tool)
}

/**
 * Every word a task verb answers to (`apply` ops use these): its name, its
 * short forms (`ls`, `done`), and the names it had before board #2440
 * (`create`, `stop`, `log`, `setTag`), which still work.
 */
export const VERB_ALIASES: Readonly<Record<string, VerbName>> = {
  ...Object.fromEntries(
    (Object.keys(VERBS) as VerbName[])
      .filter((v) => VERB_NAMES[v].noun === 'task')
      .map((v) => [VERB_NAMES[v].name, v])
  ),
  ls: 'query',
  done: 'close',
  log: 'transcript',
  'github-push': 'githubPush',
}

/** Writes `apply` does not batch: the importers (their own idempotent runs) and itself. */
const NOT_APPLIED = new Set(['apply', 'import', 'notes', 'githubPush'])

export interface ApplyResult {
  dryRun: boolean
  /** events written (or, dry, that would be) */
  events: number
  ops: { index: number; op: string; num?: number; changes: string[] }[]
}

/** One op's input as its verb takes it: `#n` / n → the task's id, `set`'s keys → pairs. */
function opInput(
  scratch: VirtaStore,
  name: VerbName,
  raw: Record<string, unknown>
): Record<string, unknown> | string {
  const input: Record<string, unknown> = { ...raw }
  if (input.id !== undefined) {
    const ref = String(input.id)
    const m = /^#?(\d+)$/.exec(ref)
    if (m) {
      const task = scratch.byNum(Number(m[1]))
      if (!task) return `no task #${m[1]}`
      input.id = task.id
    } else if (!scratch.get(ref)) return `no task ${ref}`
  }
  if (typeof input.tags === 'string')
    input.tags = input.tags
      .split(',')
      .map((t) => t.trim())
      .filter(Boolean)
  // { op: "set", id, status: "ready", priority: "high" }: every other key a pair
  if (
    name === 'setTag' &&
    input.key === undefined &&
    input.pairs === undefined
  ) {
    const { id, ...keys } = input
    return {
      id,
      pairs: Object.entries(keys).map(([k, v]) => `${k}=${v ?? ''}`),
    }
  }
  return input
}

async function runApply(
  store: VirtaStore,
  i: { ops: Record<string, unknown>[]; dryRun?: boolean }
): Promise<ApplyResult> {
  const refused: string[] = []
  const ops: ApplyResult['ops'] = []
  const counts: number[] = []
  const { events } = await store.transaction(
    async (scratch, scratchLog) => {
      for (const [index, raw] of i.ops.entries()) {
        const { op, ...rest } = raw
        const word = typeof op === 'string' ? op : ''
        const name = (
          Object.hasOwn(VERB_ALIASES, word) ? VERB_ALIASES[word] : word
        ) as VerbName
        const label = `op ${index + 1} (${word || 'no op'}${
          rest.id !== undefined ? ` ${rest.id}` : ''
        })`
        if (
          !Object.hasOwn(VERBS, name) ||
          READ_VERBS.has(name) ||
          NOT_APPLIED.has(name)
        ) {
          refused.push(`${label}: not a write apply makes`)
          continue
        }
        const input = opInput(scratch, name, rest)
        if (typeof input === 'string') {
          refused.push(`${label}: ${input}`)
          continue
        }
        const before = scratch.cursor
        const outcome = await callVerb(scratch, name, input)
        if (!outcome.ok) {
          refused.push(
            `${label}: ${outcome.error}${
              outcome.issues?.length ? ` — ${outcome.issues.join('; ')}` : ''
            }`
          )
          continue
        }
        const made = scratchLog.events.filter((e) => e.seq > before)
        counts.push(made.length)
        const task = made[0] ? scratch.get(made[0].taskId) : undefined
        ops.push({
          index,
          op: name,
          ...(task ? { num: task.num } : {}),
          changes: made.map(describeEvent),
        })
      }
      if (refused.length)
        throw new Error(
          `nothing written: ${refused.length} of ${
            i.ops.length
          } op(s) refused\n${refused.join('\n')}`
        )
    },
    {
      dry: i.dryRun === true,
      perPush: COMMIT_LIMIT,
      groups: (events) => {
        const out: VirtaEvent[][] = []
        let at = 0
        for (const n of counts) {
          if (n) out.push(events.slice(at, at + n))
          at += n
        }
        return out
      },
    }
  )
  return { dryRun: i.dryRun === true, events: events.length, ops }
}

export type VerbName = keyof typeof VERBS

/** The verbs that write nothing: a read-only MCP server serves exactly these, and the CLI's write footer skips them. */
export const READ_VERBS: ReadonlySet<string> = new Set<VerbName>([
  'versions',
  'query',
  'get',
  'transcript',
  'attachments',
  'projects',
  'brief',
  'exportPractices',
  'guide',
  'similar',
  'dispatch',
  'notices',
  'skills',
  'skill',
])
export type VerbInput<N extends VerbName> = Infer<(typeof VERBS)[N]['input']>
export type VerbResult =
  | Task
  | Task[]
  | VirtaEvent[]
  | TranscriptEntry[]
  | ProjectProfile[]
  | ImportResult
  | NotesImportResult
  | Brief
  | PushResult
  | ApplyResult
  | BodyVersion[]
  | Attachment[]
  | DispatchPlan
  | DispatchPlan[]
  | { retired: Task; successors: Task[] }
  | Guide
  | NoticeSummary[]
  | SkillSummary[]
  | SkillRead
  | Published
  | { message: string }
  | PracticesExport
  | RestorePlan
  | { applications: number; lines: string[]; dry: boolean }

export type VerbOutcome<T = VerbResult> =
  | { ok: true; result: T }
  | { ok: false; error: string; issues?: string[] }

/** Validate at the boundary, then run. Never throws for bad input. */
export async function callVerb(
  store: VirtaStore,
  name: string,
  input: unknown
): Promise<VerbOutcome> {
  // Own-property lookup only: a plain object inherits `constructor`, `__proto__`,
  // `toString`… and an inherited member must never pass as a verb.
  if (!Object.hasOwn(VERBS, name))
    return { ok: false, error: `unknown verb "${name}"` }
  const verb = VERBS[name as VerbName]
  try {
    const issues: string[] = []
    const valid = validate(input, verb.input, {
      strict: true,
      onError: (path, msg) => issues.push(path ? `${path}: ${msg}` : msg),
    })
    if (!valid)
      return { ok: false, error: `invalid input for "${name}"`, issues }
    // several tags written as one string: refused, with the fix — but
    // never on untag, which must be able to remove one that got in
    for (const key of name === 'untag' ? [] : ['tags', 'set']) {
      const list = (input as Record<string, unknown>)[key]
      if (!Array.isArray(list)) continue
      for (const tag of list) {
        const parts = typeof tag === 'string' ? joinedTags(tag) : null
        if (parts)
          return {
            ok: false,
            error: `"${tag}" is ${
              parts.length
            } tags written as one: pass them separately (the CLI's --tags is comma-separated: ${parts.join(
              ','
            )})`,
          }
      }
    }
    // a flag a shell quote folded into a value (board #3465): refused on
    // everything that writes a tag — never on untag, which removes it
    if (name !== 'untag') {
      const i = input as Record<string, unknown>
      const values = [
        ...(Array.isArray(i.tags) ? i.tags : []),
        ...(Array.isArray(i.set) ? i.set : []),
        ...(name === 'setTag'
          ? [i.value, ...(Array.isArray(i.pairs) ? i.pairs : [])]
          : []),
      ]
      for (const value of values) {
        const flag = typeof value === 'string' ? swallowedFlag(value) : null
        if (flag)
          return {
            ok: false,
            error: `"${value}" holds "${flag}", a command-line flag: a quote took it into the value — close the quote before the flag`,
          }
      }
      // a task field written as a tag (board #3626): "body=…" became a tag
      // body:… and the body never changed, with no error
      const keys = [
        ...(Array.isArray(i.tags) ? i.tags : []),
        ...(Array.isArray(i.set) ? i.set : []),
        ...(name === 'setTag'
          ? [i.key, ...(Array.isArray(i.pairs) ? i.pairs : [])]
          : []),
      ]
        .flatMap((v) =>
          typeof v === 'string' ? v.split(/\s+(?=[\w.-]+=)/) : []
        )
        .map((v) => v.split(/[:=]/)[0]!.trim().toLowerCase())
      for (const key of keys)
        if (Object.hasOwn(FIELD_VERBS, key))
          return {
            ok: false,
            error: `"${key}" is the task's ${key}, not a tag: use ${FIELD_VERBS[key]}`,
          }
    }
    const result = await (
      verb.run as (
        s: VirtaStore,
        i: unknown
      ) => Promise<VerbResult> | VerbResult
    )(store, input)
    // a copy, never the fold's own objects: a caller that edits what it
    // got back must not change state without an event (board #48)
    return { ok: true, result: structuredClone(result) }
  } catch (e) {
    // the name is the kind (`StaleReplicaError`): keep it for the caller
    return {
      ok: false,
      error:
        e instanceof Error
          ? e.name && e.name !== 'Error'
            ? `${e.name}: ${e.message}`
            : e.message
          : String(e),
    }
  }
}

export interface VerbDescription {
  name: VerbName
  /** its MCP tool name (board #2440): the verb's own for a task, project_<name> for a project */
  tool: string
  description: string
  /** Plain JSON Schema — what an MCP `tools/list` reply carries. */
  inputSchema: Record<string, unknown>
}

/** The tool catalogue, for MCP and for docs. */
export function verbCatalog(): VerbDescription[] {
  return (Object.keys(VERBS) as VerbName[]).map((name) => ({
    name,
    tool: mcpToolName(name),
    description: VERBS[name].description,
    inputSchema: VERBS[name].input.schema as unknown as Record<string, unknown>,
  }))
}
